<template>
  <div class="fcom_week_calendar" role="grid" :aria-label="__('week_view')" :style="{ '--fca-slot-height': slotHeight + 'px' }">
    <!-- Day header row -->
    <div class="fcom_week_header" role="row">
      <div class="fcom_week_time_column_header" aria-hidden="true" />
      <div
        v-for="day in weekDays"
        :key="day.dateString"
        :class="['fcom_week_day_header', { 'is-today': day.isToday }]"
        role="columnheader"
      >
        <span class="fcom_week_day_name">{{ day.shortLabel }}</span>
        <span :class="['fcom_week_day_number', { 'is-today': day.isToday }]">{{ day.dayNumber }}</span>
      </div>
    </div>

    <!-- Time grid -->
    <div ref="gridRef" class="fcom_week_grid" role="rowgroup">
      <!-- Time slot rows -->
      <div
        v-for="slot in timeSlots"
        :key="slot.value"
        class="fcom_week_time_row"
        role="row"
      >
        <div class="fcom_week_time_label" role="rowheader">
          <span>{{ slot.label }}</span>
        </div>

        <div
          v-for="day in weekDays"
          :key="day.dateString"
          :class="['fcom_week_time_cell', { 'is-today': day.isToday }]"
          role="gridcell"
          @click="$emit('select-slot', { date: day.date, hour: slot.hour, minute: slot.minute })"
        >
          <!-- Current time indicator -->
          <div
            v-if="isCurrentHourCell(day, slot)"
            class="fcom_current_time_line"
            :style="{ top: currentTimePercent + '%' }"
            aria-hidden="true"
          >
            <span class="fcom_current_time_dot" />
          </div>
        </div>
      </div>

      <!-- Event overlay layer -->
      <div class="fcom_week_events_overlay">
        <div
          v-for="day in weekDays"
          :key="day.dateString"
          :class="['fcom_week_day_column', { 'is-today': day.isToday }]"
        >
          <div
            v-for="ev in eventsForDate(day.dateString)"
            :key="ev.id"
            class="fcom_week_event_wrapper"
            :style="eventStyle(ev)"
          >
            <CalendarEvent
              :event="ev"
              :show-time="true"
              :week-view="true"
              @click="$emit('select-event', ev)"
              @hover="(p) => $emit('event-hover', { ...p, source: 'week' })"
              @leave="$emit('event-leave')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { DateTime } from 'luxon'
import { __ } from '@/utils/i18n.js'
import {
  buildWeekDays,
  buildTimeSlots,
  indexEventsByDate,
  getEventsForDate,
  getEventPosition,
} from '@/utils/calendar.js'
import CalendarEvent from './CalendarEvent.vue'

const props = defineProps({
  currentDate:  { type: Object, required: true },
  events:       { type: Array,  default: () => [] },
  weekStartDay: { type: String, default: 'monday' },
  startHour:    { type: Number, default: 0 },
  endHour:      { type: Number, default: 23 },
  slotHeight:   { type: Number, default: 50 },
})

defineEmits(['select-slot', 'select-event', 'event-hover', 'event-leave'])

const gridRef   = ref(null)
const now       = ref(DateTime.now())
let   timer     = null

onMounted(() => {
  timer = setInterval(() => { now.value = DateTime.now() }, 60000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})

const weekDays     = computed(() => buildWeekDays(props.currentDate, props.weekStartDay))
const timeSlots    = computed(() => buildTimeSlots(props.startHour, props.endHour, 60))
const indexedEvts  = computed(() => indexEventsByDate(props.events))

const currentTimePercent = computed(() => (now.value.minute / 60) * 100)

function eventsForDate(dateString) {
  return getEventsForDate(indexedEvts.value, dateString)
}

function isCurrentHourCell(day, slot) {
  return day.isToday && now.value.hour === slot.hour
}

function eventStyle(ev) {
  const pos = getEventPosition(ev, props.slotHeight, props.startHour)
  return {
    position: 'absolute',
    top:      `${pos.top}px`,
    height:   `${Math.max(pos.height, 22)}px`,
    insetInlineStart: '2px',
    width:    'calc(100% - 4px)',
    zIndex:   10,
  }
}
</script>

